import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { AnimatePresence } from "framer-motion";
import Index from "./pages/Index";
import { PageTransition } from "./components/PageTransition";
import PropertyDetail from "./pages/PropertyDetail";
import Properties from "./pages/Properties";
import Testimonials from "./pages/Testimonials";
import Team from "./pages/Team";
import Partners from "./pages/Partners";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Terms from "./pages/Terms";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import NotFound from "./pages/NotFound";
import PrivatePortal from "./pages/PrivatePortal";
import { CustomCursor } from "./components/CustomCursor";
import { RegionProvider } from "./contexts/RegionContext";
import { StickyContactButton } from "./components/StickyContactButton";
import ExitIntentPopup from "./components/ExitIntentPopup";
import FloatingAssessmentButton from "./components/FloatingAssessmentButton";

const queryClient = new QueryClient();

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      // Delay to allow page transition to complete before scrolling
      const timeoutId = setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          const navHeight = 80;
          const elementPosition = element.getBoundingClientRect().top + window.scrollY;
          
          // Premium smooth scroll with custom easing via CSS
          window.scrollTo({
            top: elementPosition - navHeight,
            behavior: "smooth",
          });
        }
      }, 150);
      return () => clearTimeout(timeoutId);
    } else {
      // Instant scroll to top on new page navigation
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [pathname, hash]);

  return null;
};

const AnimatedRoutes = () => {
  const location = useLocation();
  
  return (
    <AnimatePresence mode="wait" initial={false}>
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PageTransition><Index /></PageTransition>} />
        <Route path="/property/:id" element={<PageTransition><PropertyDetail /></PageTransition>} />
        <Route path="/properties" element={<PageTransition><Properties /></PageTransition>} />
        <Route path="/testimonials" element={<PageTransition><Testimonials /></PageTransition>} />
        <Route path="/team" element={<PageTransition><Team /></PageTransition>} />
        <Route path="/partners" element={<PageTransition><Partners /></PageTransition>} />
        <Route path="/privacy-policy" element={<PageTransition><PrivacyPolicy /></PageTransition>} />
        <Route path="/terms" element={<PageTransition><Terms /></PageTransition>} />
        <Route path="/blog" element={<PageTransition><Blog /></PageTransition>} />
        <Route path="/blog/:slug" element={<PageTransition><BlogPost /></PageTransition>} />
        <Route path="/private-portal" element={<PageTransition><PrivatePortal /></PageTransition>} />
        <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
      </Routes>
    </AnimatePresence>
  );
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <RegionProvider>
          <TooltipProvider>
            <CustomCursor />
            <Toaster />
            <Sonner />
            <ScrollToTop />
            <AnimatedRoutes />
            <StickyContactButton />
            <FloatingAssessmentButton />
            <ExitIntentPopup />
          </TooltipProvider>
        </RegionProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
};

export default App;
